import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, Globe, Users, Sparkles, Zap, TrendingUp } from 'lucide-react';

const Analysis = () => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);

  const steps = [
    { icon: Brain, text: "Analyzing facial features & symmetry...", duration: 2000, color: "text-purple-600" },
    { icon: Globe, text: "Comparing with 50+ global beauty standards...", duration: 2000, color: "text-blue-600" },
    { icon: Users, text: "Calculating demographic preferences...", duration: 2000, color: "text-pink-600" },
    { icon: Sparkles, text: "Generating your personalized report...", duration: 2000, color: "text-yellow-600" }
  ];

  useEffect(() => {
    const image = localStorage.getItem('uploadedImage');
    if (!image) {
      navigate('/');
      return;
    }
    setUploadedImage(image);

    // Simulate analysis progress
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => navigate('/results'), 500);
          return 100;
        }
        return prev + 1;
      });
    }, 80);

    // Update steps
    const stepInterval = setInterval(() => {
      setCurrentStep(prev => {
        if (prev >= steps.length - 1) {
          clearInterval(stepInterval);
          return prev;
        }
        return prev + 1;
      });
    }, 2000);

    return () => {
      clearInterval(interval);
      clearInterval(stepInterval);
    };
  }, [navigate]);

  const CurrentIcon = steps[currentStep].icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center p-6 relative overflow-hidden">
      {/* Animated background elements */}
      <motion.div
        animate={{ 
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360]
        }}
        transition={{ duration: 20, repeat: Infinity }}
        className="absolute top-10 right-10 w-64 h-64 bg-purple-200/30 rounded-full blur-3xl"
      />
      <motion.div
        animate={{ 
          scale: [1.2, 1, 1.2],
          rotate: [360, 180, 0]
        }}
        transition={{ duration: 15, repeat: Infinity }}
        className="absolute bottom-10 left-10 w-96 h-96 bg-pink-200/30 rounded-full blur-3xl"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-3xl w-full bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-12 relative z-10 border-2 border-purple-100"
      >
        {/* Photo Preview */}
        {uploadedImage && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center mb-8"
          >
            <div className="relative">
              <img 
                src={uploadedImage} 
                alt="Analyzing" 
                className="w-32 h-32 rounded-2xl object-cover shadow-xl ring-4 ring-purple-200"
              />
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="absolute -top-2 -right-2 bg-purple-600 rounded-full p-2"
              >
                <Zap className="h-5 w-5 text-white" />
              </motion.div>
            </div>
          </motion.div>
        )}

        <div className="text-center mb-8">
          <motion.div
            animate={{ 
              rotate: 360,
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              rotate: { duration: 2, repeat: Infinity, ease: "linear" },
              scale: { duration: 1, repeat: Infinity }
            }}
            className="inline-block mb-6"
          >
            <CurrentIcon className={`h-24 w-24 ${steps[currentStep].color}`} />
          </motion.div>
          
          <h2 className="text-4xl font-bold mb-4 text-gradient">
            AI Analysis in Progress
          </h2>
          
          <motion.p
            key={currentStep}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl text-gray-700 mb-4 font-semibold"
          >
            {steps[currentStep].text}
          </motion.p>

          <div className="inline-flex items-center gap-2 bg-purple-100 px-4 py-2 rounded-full">
            <TrendingUp className="h-4 w-4 text-purple-600" />
            <span className="text-sm font-semibold text-purple-700">
              Processing {progress}% complete
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative w-full h-6 bg-gray-200 rounded-full overflow-hidden mb-6 shadow-inner">
          <motion.div
            className="absolute top-0 left-0 h-full gradient-primary"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
          <motion.div
            animate={{ x: ['-100%', '200%'] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            className="absolute top-0 left-0 h-full w-1/3 bg-gradient-to-r from-transparent via-white/40 to-transparent"
          />
        </div>
        
        <p className="text-center text-2xl text-gray-800 font-bold mb-8">
          {progress}%
        </p>

        {/* Feature List */}
        <div className="space-y-4">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ 
                opacity: index <= currentStep ? 1 : 0.4,
                x: 0 
              }}
              className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                index <= currentStep 
                  ? 'bg-gradient-to-r from-purple-50 to-pink-50 shadow-md' 
                  : 'bg-gray-50'
              }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg ${
                index < currentStep 
                  ? 'bg-green-500' 
                  : index === currentStep 
                  ? 'bg-purple-600 animate-pulse' 
                  : 'bg-gray-300'
              }`}>
                {index < currentStep ? (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                  >
                    ✓
                  </motion.div>
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>
              <span className={`text-lg ${
                index <= currentStep ? 'text-gray-800 font-semibold' : 'text-gray-400'
              }`}>
                {step.text}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Fun fact */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-8 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl"
        >
          <p className="text-sm text-blue-800 text-center">
            💡 <strong>Did you know?</strong> Our AI analyzes over 50 facial features and compares them with beauty standards from 50+ countries worldwide!
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Analysis;